import { MediaDisplay } from './MediaDisplay';

/**
 * MetaStoryMockup - Instagram/Facebook Story 9:16 Ad Mockup
 *
 * Dimensions: 420px × 747px (9:16 aspect ratio)
 *
 * Structure (matching Instagram Story ad format):
 * - Top Header: Profile picture, username, "Sponsored" label, close button
 * - Full-screen Background: 9:16 image/video
 * - Right Side Actions: Heart, message, share icons
 * - Bottom CTA: Swipe-up style "Learn More" button
 *
 * Usage:
 *   <MetaStoryMockup
 *     username="Ford"
 *     caption="Discover the all-new Ford..."
 *     imageUrl="/path/to/image.jpg"  // Can be image or video URL
 *     ctaText="Learn More"
 *   />
 */

interface MetaStoryMockupProps {
  username?: string;
  profilePicUrl?: string;
  caption?: string;
  imageUrl?: string;
  contentType?: string | null;
  ctaText?: string;
  websiteUrl?: string;
}

export function MetaStoryMockup({
  username = 'Ford',
  profilePicUrl,
  imageUrl,
  contentType,
  ctaText = 'Learn More',
  websiteUrl = 'ford.com',
}: MetaStoryMockupProps) {
  return (
    <div className="relative w-full h-full bg-black overflow-hidden">
      {/* Full-screen Background Image/Video */}
      <div className="absolute inset-0">
        {imageUrl ? (
          <MediaDisplay
            url={imageUrl}
            contentType={contentType}
            alt="Story ad"
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gray-800 flex items-center justify-center">
            <div className="text-center text-white/50">
              <div className="text-4xl font-bold mb-2">9:16</div>
              <div className="text-sm">1080x1920px recommended</div>
            </div>
          </div>
        )}
      </div>

      {/* Gradient overlays for better text visibility */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/60 pointer-events-none" />

      {/* Top Header */}
      <div
        className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between"
        style={{ padding: '16px 12px 0' }}
      >
        {/* Left: Profile & Username */}
        <div className="flex items-center" style={{ gap: '10px' }}>
          {/* Profile Picture - Circular with gradient border */}
          <div
            className="rounded-full p-[2px]"
            style={{
              background: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)',
            }}
          >
            <div
              className="bg-black rounded-full overflow-hidden flex items-center justify-center"
              style={{ width: '32px', height: '32px' }}
            >
              {profilePicUrl ? (
                <img
                  src={profilePicUrl}
                  alt={username}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-white font-bold text-sm">
                  {username.charAt(0).toUpperCase()}
                </span>
              )}
            </div>
          </div>

          {/* Username & Sponsored */}
          <div className="flex items-center gap-2">
            <span
              className="text-white font-semibold"
              style={{ fontSize: '13px', lineHeight: '18px' }}
            >
              {username}
            </span>
            <span className="text-white/80" style={{ fontSize: '13px' }}>
              •
            </span>
            <span
              className="text-white/80"
              style={{ fontSize: '13px', lineHeight: '18px' }}
            >
              Sponsored
            </span>
          </div>
        </div>

        {/* Right: Close button */}
        <button className="text-white/90 hover:text-white">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18 6L6 18M6 6L18 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {/* Right Side Actions */}
      <div
        className="absolute z-20 flex flex-col items-center"
        style={{ right: '12px', bottom: '120px', gap: '20px' }}
      >
        {/* Heart */}
        <button className="flex flex-col items-center">
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {/* Message */}
        <button className="flex flex-col items-center">
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {/* Share/Send */}
        <button className="flex flex-col items-center">
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {/* More options */}
        <button className="flex flex-col items-center">
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="12" cy="12" r="1" fill="white" />
            <circle cx="12" cy="5" r="1" fill="white" />
            <circle cx="12" cy="19" r="1" fill="white" />
          </svg>
        </button>
      </div>

      {/* Bottom Section */}
      <div className="absolute bottom-0 left-0 right-0 z-20" style={{ padding: '0 12px 16px' }}>
        {/* Caption — commented out: 9:16 story ads don't display primary text in the placement
        {caption && (
          <p
            className="text-white text-sm mb-3 line-clamp-2"
            style={{ fontSize: '14px', lineHeight: '18px' }}
          >
            {caption}
          </p>
        )} */}

        {/* CTA Button - Instagram Story Style */}
        <div className="flex flex-col items-center">
          {/* Swipe up indicator */}
          <div className="flex flex-col items-center mb-2">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="animate-bounce"
            >
              <path
                d="M12 19V5M5 12L12 5L19 12"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          {/* CTA Button */}
          <button
            className="w-full flex items-center justify-center text-white font-semibold"
            style={{
              height: '44px',
              background: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(8px)',
              borderRadius: '22px',
              fontSize: '15px',
              letterSpacing: '0.3px',
            }}
          >
            {ctaText}
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="ml-2"
            >
              <path
                d="M5 12H19M12 5L19 12L12 19"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          {/* Website URL below button */}
          <span
            className="text-white/70 mt-2"
            style={{ fontSize: '12px' }}
          >
            {websiteUrl}
          </span>
        </div>
      </div>
    </div>
  );
}
